/**
 * CalendarConnectionsCard Component
 * Main settings card for managing calendar connections (Google/Microsoft).
 *
 * Features:
 * - Lists connected calendar accounts
 * - Connect new calendar via OAuth popup
 * - Disconnect existing connections
 * - Manage calendar sync preferences
 * - Manual sync trigger
 */

import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { CalendarProvider, CalendarConnectionDto } from '@signalvote/shared';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useCalendarConnections,
  useStartCalendarConnection,
  useDisconnectCalendar,
  useTriggerCalendarSync,
  useUpdateCalendarConnection,
} from '@/hooks/queries/useCalendar';
import { openOAuthPopup } from '@/utils/oauthPopup';
import { useDialogState } from '@/hooks/useDialogState';
import { selectedCampaignKey } from '@/lib/data';
import { CalendarConnectionItem } from './CalendarConnectionItem';
import { CalendarSelectorDialog } from './CalendarSelectorDialog';
import { ConnectCalendarButton } from './ConnectCalendarButton';
import { CalendarSettingsReadNotice } from './CalendarSettingsReadNotice';

// ============================================================================
// Component
// ============================================================================

export function CalendarConnectionsCard() {
  const queryClient = useQueryClient();

  const { read: connectionsRead, isSyncing } = useCalendarConnections();
  const connections = connectionsRead.status === 'ready' ? connectionsRead.data : undefined;

  // Mutations
  const startConnection = useStartCalendarConnection();
  const disconnectCalendar = useDisconnectCalendar();
  const triggerSync = useTriggerCalendarSync();
  const updateConnection = useUpdateCalendarConnection();

  // Collect all used colors across connections and their sub-calendars
  const usedColors = useMemo(() => {
    const colors = new Set<string>();
    connections?.forEach((conn) => {
      if (conn.color) colors.add(conn.color.toLowerCase());
      conn.syncPreferences?.forEach((pref) => {
        if (pref.color) colors.add(pref.color.toLowerCase());
      });
    });
    return colors;
  }, [connections]);

  // Track which connection is being disconnected
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  // Track which connection just completed OAuth (for inline setup)
  const [newConnectionId, setNewConnectionId] = useState<string | null>(null);

  // Dialog state for calendar selector
  const calendarSelectorDialog = useDialogState<CalendarConnectionDto>();

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Initiates OAuth flow for the selected provider
   */
  const handleConnect = async (provider: CalendarProvider) => {
    // Redirect URI must point to the BACKEND callback endpoint
    // Backend will exchange the code for tokens, then redirect to frontend
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
    const redirectUri = `${apiUrl}/api/calendar/callback/${provider.toLowerCase()}`;

    try {
      // Get authorization URL from backend
      const response = await startConnection.mutateAsync({ provider, redirectUri });

      // Open OAuth popup
      const result = await openOAuthPopup({ url: response.authorizationUrl });

      if (result.success) {
        // The OAuth callback saved the connection in the popup, so no write set reached this page.
        queryClient.invalidateQueries({ queryKey: selectedCampaignKey(['calendar', 'connections']) });
        // Trigger initial sync to fetch calendar events
        triggerSync.mutate();
        // Track new connection for inline setup
        setNewConnectionId(result.connectionId ?? null);
      } else if (result.error) {
        // User cancelled or error occurred
        console.warn('OAuth flow cancelled or failed:', result.error);
      }
    } catch (error) {
      console.error('Failed to start calendar connection:', error);
    }
  };

  /**
   * Handles reconnect for expired token
   */
  const handleReconnect = (connection: CalendarConnectionDto) => {
    handleConnect(connection.provider);
  };

  /**
   * Opens calendar selector dialog for a connection
   */
  const handleManageCalendars = (connection: CalendarConnectionDto) => {
    calendarSelectorDialog.open(connection);
  };

  /**
   * Disconnects a calendar with confirmation
   */
  const handleDisconnect = async (connection: CalendarConnectionDto) => {
    const confirmed = window.confirm(
      `Are you sure you want to disconnect ${connection.accountEmail}? This will stop syncing events from this account.`
    );

    if (!confirmed) return;

    setDisconnectingId(connection.id);
    try {
      await disconnectCalendar.mutateAsync(connection.id);
    } catch (error) {
      console.error('Failed to disconnect calendar:', error);
    } finally {
      setDisconnectingId(null);
    }
  };

  /**
   * Triggers a manual sync of all connected calendars
   */
  const handleSyncNow = () => {
    triggerSync.mutate();
  };

  // ============================================================================
  // Render
  // ============================================================================

  const hasConnections = connections && connections.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Calendar Connections</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {hasConnections && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncNow}
                disabled={triggerSync.isPending || isSyncing}
                className="gap-1.5"
              >
                {triggerSync.isPending || isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {isSyncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            )}
            <ConnectCalendarButton
              onConnect={handleConnect}
              disabled={startConnection.isPending}
            />
          </div>
        </CardHeader>

        <CardContent>
          {connectionsRead.status === 'failed' || connectionsRead.status === 'offline' ? (
            <CalendarSettingsReadNotice read={connectionsRead} subject="calendar connections" />
          ) : !connections ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : hasConnections ? (
            <div className="space-y-3">
              {connections.map((connection) => {
                // Find if another connection already has a write calendar
                const existingWrite = connections
                  .filter(c => c.id !== connection.id)
                  .flatMap(c => c.syncPreferences ?? [])
                  .find(p => p.isPrimaryWriteCalendar);

                return (
                  <CalendarConnectionItem
                    key={connection.id}
                    connection={connection}
                    isNewConnection={connection.id === newConnectionId}
                    existingWriteCalendarName={existingWrite?.calendarName}
                    onSetupComplete={() => setNewConnectionId(null)}
                    onManageCalendars={() => handleManageCalendars(connection)}
                    onReconnect={() => handleReconnect(connection)}
                    onDisconnect={() => handleDisconnect(connection)}
                    onUpdateConnection={(update) =>
                      updateConnection.mutate({
                        connectionId: connection.id,
                        request: update,
                      })
                    }
                    usedColors={usedColors}
                    isDisconnecting={disconnectingId === connection.id}
                  />
                );
              })}

              {/* Warning when no connection has a write calendar */}
              {!connections.some(c => c.syncPreferences?.some(p => p.isPrimaryWriteCalendar)) && (
                <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 mt-1">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">
                    No write calendar selected. Open <strong>Calendars</strong> on any account to pick one for shifts &amp; tasks.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                No calendars connected
              </p>
              <p className="text-xs text-muted-foreground">
                Connect your Google or Microsoft calendar to see your availability and sync shifts.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar Selector Dialog */}
      <CalendarSelectorDialog
        open={calendarSelectorDialog.isOpen}
        onClose={calendarSelectorDialog.close}
        connection={calendarSelectorDialog.data}
        usedColors={usedColors}
      />
    </>
  );
}

export default CalendarConnectionsCard;
